import { ReactElement, useEffect } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import {
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiFlyoutFooter,
  EuiSpacer,
  EuiOverlayMask,
  EuiToolTip,
  useEuiTheme,
} from "@elastic/eui";
import { FDFlyoutTitle } from "components/Title/FDFlyoutTitle";
import { createUseStyles } from "react-jss";
import Button from "components/Buttons/Button";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { SparkleIcon } from "components/Icons";

const useStyles = createUseStyles({
  flyout: ({ zIndex }) => ({
    zIndex: `${zIndex} !important`,
    "& .euiFlyout__closeButton": {
      right: "12px !important",
      top: "12px !important",
      color: "#fff",
    },
  }),
  flyoutHeader: ({ isAdvisor }: any) => ({
    paddingBlockEnd: "13px!important",
    paddingBlockStart: "13px!important",
    padding: "4px",
    background: "var(--flyout-header)",
    position: "relative",
  }),
});

interface FlyoutType {
  headerChildren?: any;
  children: any;
  size?: string;
  title: string | ReactElement;
  titleSize?: "s" | "m" | "l" | "xs" | "xxxs" | "xxs";
  onClose: (event: MouseEvent | TouchEvent | KeyboardEvent) => void;
  onAsk?: () => void | undefined;
  footerChildren?: any;
  tabs?: any;
  side?: "left" | "right";
  type?: "overlay" | "push";
  sideChildren?: ReactElement | undefined;
  zIndex?: number;
}

export const Flyout = ({
  headerChildren,
  children,
  size = "1050px",
  title = "",
  titleSize,
  onClose,
  footerChildren = null,
  tabs = null,
  side = "right",
  type = "overlay",
  sideChildren,
  zIndex = 9000,
}: FlyoutType) => {
  const theme = useEuiTheme();
  const dispatch = useDispatch();
  const isAdvisor = typeof title !== "string";
  const classes = useStyles({ isAdvisor, zIndex });
  const { isAskingAdvisor } = useSelector(globalFilterSelector, shallowEqual);

  useEffect(() => {
    if (side !== "left" && children) {
      document.body.style.overflowY = "hidden";
    }
    return () => {
      document.body.style.overflowY = "scroll";
    };
  }, [children]);

  const handleAsk = () => {
    // isAskingAdvisor
    dispatch({
      type: globalFilterTypes.SET_ASK_ADVISOR_FLAG,
      payload: true,
    });
  };

  const handleClose = (e: any) => {
    if (isAskingAdvisor) {
      dispatch({
        type: globalFilterTypes.SET_ASK_ADVISOR_FLAG,
        payload: false,
      });

      dispatch({
        payload: null,
        type: globalFilterTypes.SET_WISDOM_ADVISOR_CONTEXT,
      });
    }
    onClose(e);
  };

  return (
    <div
      style={{
        position: "relative",
        zIndex: `${zIndex} !important`,
      }}>
      {side === "right" && (
        <EuiOverlayMask>
          {sideChildren && sideChildren}
          <EuiFlyout
            onClose={handleClose}
            outsideClickCloses={!isAdvisor}
            size={size}
            side={side}
            type={type}
            className={classes.flyout}
            ownFocus={false}
            style={{
              insetBlockStart: 0,
              backgroundColor:
                typeof title !== "string" ? "var(--card-background)" : "var(--flyout-bg)",
            }}>
            <EuiFlyoutHeader className={classes.flyoutHeader} hasBorder>
              {typeof title === "string" && <FDFlyoutTitle title={title} titleSize={titleSize} />}
              {typeof title !== "string" && title}

              {children?.props?.withAdvisor && (
                <div
                  style={{
                    position: "absolute",
                    right: "45px",
                    top: "8px",
                  }}>
                  <EuiToolTip content="Analyze with Advisor">
                    {/* <Button
                      iconType={"sparkles"}
                      type="empty"
                      text="Ask WISdom Advisor"
                      onClick={handleAsk}
                      style={{
                        color: "#f8bf40",
                        backgroundColor: "#f8bf4022",
                      }}
                    /> */}
                    <span style={{ paddingRight: "12px" }}>
                      <Button
                        // iconType={"sparkles"}
                        type="empty"
                        text={
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ paddingRight: "4px", paddingTop: "4px" }}>
                              <SparkleIcon />
                            </span>
                            <b style={{ color: "#fff" }}>WIS</b>
                            <span style={{ color: "var(--brand-color)", fontWeight: 800 }}>
                              dom
                            </span>
                            <span
                              style={{
                                color: "#fff",
                                paddingLeft: "4px",
                                fontSize: "var(--fs13)",
                                fontWeight: 600,
                              }}>
                              Advisor
                            </span>
                          </div>
                        }
                        onClick={() => {
                          // setOpen(true)
                        }}
                        style={{ color: "#1b8dcd" }}
                      />
                    </span>
                  </EuiToolTip>
                </div>
              )}

              {headerChildren && <div>{headerChildren}</div>}
            </EuiFlyoutHeader>
            {tabs && (
              <div
                style={{
                  margin: "0 24px",
                }}>
                <EuiSpacer size="m" />
                {tabs}
              </div>
            )}
            <>
              <EuiFlyoutBody>{children}</EuiFlyoutBody>
              {footerChildren && (
                <EuiFlyoutFooter style={{ backgroundColor: "var(--app-background)" }}>
                  {footerChildren}
                </EuiFlyoutFooter>
              )}
            </>
          </EuiFlyout>
        </EuiOverlayMask>
      )}
      {side === "left" && (
        <EuiFlyout
          onClose={onClose}
          outsideClickCloses
          size={size}
          side={side}
          type={type}
          className={classes.flyout}
          ownFocus={false}
          style={{
            insetBlockStart: 0,
            backgroundColor: "var(--card-background)",
          }}
          includeFixedHeadersInFocusTrap={false}>
          <EuiFlyoutHeader className={classes.flyoutHeader} hasBorder>
            {typeof title === "string" && <FDFlyoutTitle title={title} titleSize={titleSize} />}
            {typeof title !== "string" && title}
            {headerChildren && <div>{headerChildren}</div>}
          </EuiFlyoutHeader>
          {tabs && (
            <div style={{ margin: "0 24px" }}>
              <EuiSpacer size="m" />
              {tabs}
            </div>
          )}
          <>
            <EuiFlyoutBody style={{ backgroundColor: "var(--card-background)" }}>
              {children}
            </EuiFlyoutBody>
            {footerChildren && (
              <EuiFlyoutFooter style={{ backgroundColor: "var(--app-background)" }}>
                {footerChildren}
              </EuiFlyoutFooter>
            )}
          </>
        </EuiFlyout>
      )}
    </div>
  );
};
